import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Box, Typography, Button } from '@material-ui/core';
import { Add as AddIcon, KeyboardArrowLeft, Edit as EditIcon, SwapVert as SwapVertIcon } from '@material-ui/icons';
import { DraggableList, DraggableListItem } from '@campfire/draggable-list';
import { useRouteMatch, useParams, useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { HoverLink } from '@campfire/hover-link';
import Icon from '@mdi/react';
import { ResourcesListItem } from '../catalogue-landing-page/ResourcesListItem';
import { SupportCategory } from '../__generated__/SupportCategory';
import { SupportResource } from '../__generated__/SupportResource';
import { useSupportContext } from '../SupportContext';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { iconList } from '../../../../common/icons/RoleIconList';

const toolbarButtonStyle = {
  backgroundColor: 'transparent',
  color: '#646464',
  border: '1px solid #d9d9d9',
  borderRadius: 4,
  padding: '4.5px 12px',
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'none' as const,
  minWidth: 'auto',
  display: 'flex',
  alignItems: 'center',
};

interface Props {
  selectedCategory: SupportCategory;
}
const CategoryViewer = (props: Props) => {
  const { selectedCategory } = props;
  const { isAdmin, setInEditCategoryMode, runReorderResources } = useSupportContext();
  const { isMobile, isSm } = useCampfireTheme();
  const match = useRouteMatch();
  const { categoryId } = useParams();
  const location = useLocation();
  const [reorderMode, setReorderMode] = useState(false);

  const categoryColour = (selectedCategory as any).colour;
  const categoryIcon = (selectedCategory as any).icon;

  const sortedResources = useMemo(() => {
    const resources = [...selectedCategory.supportResources];
    resources.sort((a, b) => {
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    });
    return resources;
  }, [selectedCategory.supportResources]);

  const [orderedResources, setOrderedResources] = useState(sortedResources);

  useEffect(() => {
    setOrderedResources(sortedResources);
  }, [sortedResources]);

  const handleReorder = useCallback(
    (startIdx: number, endIdx: number, reorderFn: (list: any[], s: number, e: number) => any[]) => {
      const reordered = reorderFn(orderedResources, startIdx, endIdx) as SupportResource[];
      setOrderedResources(reordered);
      runReorderResources(
        reordered.map((res, idx) => ({
          supportResourceId: res.supportResourceId,
          sortOrder: idx,
        }))
      );
    },
    [orderedResources, runReorderResources]
  );

  const displayResources = reorderMode ? orderedResources : sortedResources;

  return (
    <Box display='flex' flexDirection='column' flex={1} height='100%' overflow='auto'>
      {/* Top bar with back link and actions */}
      <Box
        style={{
          paddingLeft: isMobile || isSm ? 10 : 25,
          paddingRight: 10,
          paddingTop: 10,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <HoverLink
          to={categoryId ? location.pathname.replace(`/${categoryId}`, '') : location.pathname}
          color='textPrimary'
          hoverColor='textPrimary'
          disableUnderline
        >
          <Box display='flex' alignItems='center'>
            <KeyboardArrowLeft style={{ fontSize: 20, color: '#666' }} />
            <Typography style={{ fontSize: 14, fontWeight: 600, color: '#666' }}>{'Back to Resources'}</Typography>
          </Box>
        </HoverLink>
        <Box display='flex' alignItems='center' style={{ gap: 8 }}>
          {isAdmin ? (
            <Button
              onClick={() => setReorderMode(!reorderMode)}
              style={{
                ...toolbarButtonStyle,
                backgroundColor: reorderMode ? '#7047EB' : 'transparent',
                color: reorderMode ? '#fff' : '#646464',
              }}
            >
              <SwapVertIcon style={{ fontSize: 16, marginRight: 4 }} />
              {reorderMode ? 'Done' : 'Reorder'}
            </Button>
          ) : null}
          {isAdmin ? (
            <Button
              data-track='resources-category-view-edit-category-button'
              onClick={() => setInEditCategoryMode(true)}
              style={toolbarButtonStyle}
            >
              <EditIcon style={{ fontSize: 14, marginRight: 4 }} />
              {'Edit Category'}
            </Button>
          ) : null}
          {isAdmin ? (
            <Link
              data-track='resources-category-view-add-resource-button'
              to={{
                pathname: match?.url.replace(`${selectedCategory.supportCategoryId}`, `create-resource`) ?? '',
                search: `?catId=${selectedCategory.supportCategoryId}`,
                state: { returnSlug: match?.url },
              }}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <Button
                style={{
                  backgroundColor: '#7047EB',
                  color: '#fff',
                  borderRadius: 4,
                  padding: '4.5px 12px',
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: 'none',
                  minWidth: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <AddIcon style={{ fontSize: 16, marginRight: 4 }} />
                {'Add Resource'}
              </Button>
            </Link>
          ) : null}
        </Box>
      </Box>

      {/* Category title with icon */}
      <Box
        display='flex'
        alignItems='center'
        style={{
          paddingLeft: isMobile || isSm ? 10 : 60,
          paddingRight: isMobile || isSm ? 10 : 60,
          paddingTop: 24,
          paddingBottom: 24,
        }}
      >
        {categoryIcon && iconList[categoryIcon] ? (
          <Box
            display='flex'
            alignItems='center'
            justifyContent='center'
            mr={2}
            style={{
              width: 48,
              height: 48,
              borderRadius: 10,
              backgroundColor: `${categoryColour || '#7047EB'}20`,
              flexShrink: 0,
            }}
          >
            <Icon path={iconList[categoryIcon]} size={0.85} color={categoryColour || '#7047EB'} />
          </Box>
        ) : null}
        <Typography style={{ fontSize: 24, fontWeight: 700, color: '#333' }}>{selectedCategory.title}</Typography>
      </Box>

      {/* Resources list */}
      <Box
        style={{
          paddingLeft: isMobile || isSm ? 12 : 60,
          paddingRight: isMobile || isSm ? 12 : 60,
          paddingBottom: 40,
        }}
      >
        {/* Resources count */}
        <Typography style={{ fontSize: 13, fontWeight: 600, color: '#999', marginBottom: 12 }}>
          {`${selectedCategory.supportResources.length} Resource${
            selectedCategory.supportResources.length !== 1 ? 's' : ''
          }`}
        </Typography>

        {/* Resource items */}
        <Box
          style={{
            border: '1px solid #e8e8e8',
            borderRadius: 12,
            overflow: 'hidden',
          }}
        >
          {displayResources.length > 0 ? (
            reorderMode ? (
              <DraggableList onReorder={handleReorder}>
                {orderedResources.map((resource, idx) => (
                  <DraggableListItem
                    key={resource.supportResourceId}
                    id={resource.supportResourceId}
                    index={idx}
                    style={{ padding: 0, background: 'transparent' }}
                  >
                    <Box width='100%'>
                      <ResourcesListItem
                        resource={resource}
                        borderBottom={idx !== orderedResources.length - 1}
                        showUploadDate
                        showDragHandle
                      />
                    </Box>
                  </DraggableListItem>
                ))}
              </DraggableList>
            ) : (
              sortedResources.map((resource, idx) => (
                <Link
                  data-track='resources-category-view-resource-item'
                  key={resource.supportResourceId}
                  to={{
                    pathname: match !== null ? `${match.url}/${resource.supportResourceId}` : '',
                    state: { returnSlug: match?.url },
                  }}
                  style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                >
                  <ResourcesListItem
                    resource={resource}
                    borderBottom={idx !== sortedResources.length - 1}
                    showUploadDate
                  />
                </Link>
              ))
            )
          ) : (
            <Box style={{ padding: '32px 24px' }} display='flex' justifyContent='center'>
              <Typography style={{ fontSize: 13, color: '#999' }}>{'No resources in this category yet.'}</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export { CategoryViewer };
